import { useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  TextField,
  FormControl,
  Button,
  CircularProgress,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VideocamIcon from "@mui/icons-material/Videocam";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { AxiosService } from "../../../services/axios/axiosService";

const CandidateAccess = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get("token");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [interviewData, setInterviewData] = useState(null);
  const [emailError, setEmailError] = useState(false);
  const [emailHelperText, setEmailHelperText] = useState("");
  const [fetchFailed, setFetchFailed] = useState(false);
  const [fetchFailedMessage, setFetchFailedMessage] = useState("");

  const fetchInterviewData = async () => {
    try {
      setLoading(true);
      setFetchFailed(false);
      const response = await AxiosService.get(
        `/api/Interview/start?token=${token}`,
      );
      // If interview is already completed, do not allow login
      if (response?.status === "Completed") {
        setFetchFailed(true);
        setFetchFailedMessage(
          "This interview has already been completed and cannot be accessed again.",
        );
        setInterviewData(null);
        return;
      }

      setInterviewData(response);
    } catch (err) {
      setFetchFailed(true);
      setFetchFailedMessage(err?.message || "Something went wrong. Please check your link or try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterviewData();
  }, [token]);

  useEffect(() => {
    if (interviewData) {
      setFullName(interviewData?.candidateName);
    }
  }, [interviewData]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailError(false);
    setEmailHelperText("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!email) {
      setEmailError(true);
      setEmailHelperText("Email address is required");
      return;
    } else if (email !== interviewData?.candidateEmail) {
      setEmailError(true);
      setEmailHelperText("Email address does not match the registered email address.");
      return;
    }
    navigate(`/candidate-disclaimer/${token}` , { state: { interviewId: interviewData?.interviewId } });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <Container maxWidth="sm">
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CircularProgress size={48} />
            <Typography variant="body2" sx={{ color: "#666" }}>
              Loading...
            </Typography>
          </Box>
        ) : fetchFailed ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "white",
              padding: "40px",
              borderRadius: "14px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              width: "100%",
            }}
          >
            <Box
              sx={{
                backgroundColor: "error.light",
                borderRadius: "12px",
                width: "60px",
                height: "64px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <ErrorOutlineRoundedIcon sx={{ color: "#fff", fontSize: "32px" }} />
            </Box>
           
            <Typography
              variant="body1"
              fontWeight={500}
            >
              {fetchFailedMessage}
            </Typography>
            
            
          </Box>
        ) : (
      <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "white",
            padding: "40px",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Box
            sx={{
              backgroundColor: "primary.main",
              borderRadius: "12px",
              width: "64px",
              height: "64px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            <VideocamIcon sx={{ color: "white", fontSize: "32px" }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              marginBottom: "8px",
              color: "#1a1a1a",
              
            }}
          >
            IGNA Scout Interview
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            mb={4}
          >
            Welcome to your AI-assisted interview
          </Typography>

          <form onSubmit={handleSubmit} style={{ width: "100%" }}>
            <FormControl fullWidth sx={{ mb: 2.5 }}>
              <Typography
                component="label"
                htmlFor="full-name"
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#333",
                  mb: 0.75,
                  display: "block",
                }}
              >
                Full Name <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <TextField
                disabled
                id="full-name"
                variant="outlined"
                required
                placeholder="e.g. Jane Doe"
                value={fullName}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderRadius: 2,
                      borderColor: "#d1d5db",
                    },
                    "&:hover fieldset": {
                      borderColor: "#9ca3af",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2563eb",
                    },
                  },
                }}
              />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2.5 }}>
              <Typography
                component="label"
                htmlFor="email"
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#333",
                  mb: 0.75,
                  display: "block",
                }}
              >
                Email Address <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <TextField
                id="email"
                type="email"
                variant="outlined"
                required
                placeholder="e.g. name@example.com"
                value={email}
                onChange={handleEmailChange}
                error={emailError}
                helperText={emailHelperText}
                FormHelperTextProps={{
                  sx: {
                    fontSize: "12px",
                    mt: 0.5,
                    ml: 0,
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderRadius: 2,
                      borderColor: "#d1d5db",
                    },
                    "&:hover fieldset": {
                      borderColor: "#9ca3af",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2563eb",
                    },
                  },
                }}
              />
            </FormControl>

            <Button
              type="submit"
              disabled={loading || fetchFailed}
              variant="contained"
              size="large"
              fullWidth
              sx={{
                backgroundColor: "primary.main",
                mb: 3,
                p: 1.5,
              }}
              endIcon={
                loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <ArrowForwardIcon />
                )
              }
            >
              {loading ? "Loading..." : "Start Interview"}
            </Button>
          </form>

        </Box>
        )}
      </Container>
    </Box>
  );
};

export default CandidateAccess;
